import React from 'react';
import { Truck, Package, MapPin, Clock, DollarSign } from 'lucide-react';

const ShippingPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-sky-50 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 
            className="text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Shipping Policy
          </h1>
          <p className="text-slate-600">Last updated: January 2026</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Free Shipping Banner */}
        <div className="bg-sky-500 text-white rounded-2xl p-6 mb-12 flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Truck size={28} />
          </div>
          <div>
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Free Shipping on Orders Over $50!
            </h2>
            <p className="text-sky-100" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              Enjoy free standard shipping on all orders over $50 within the continental United States.
            </p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none" style={{ fontFamily: 'Quicksand, sans-serif' }}>
          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            1. Shipping Destinations
          </h2>
          <p className="text-slate-600 mb-6">
            We currently ship to all 50 United States. We do not ship internationally at this time. For customers outside the US, please check back as we plan to expand our shipping destinations in the future.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            2. Shipping Options & Rates
          </h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-slate-50 rounded-xl overflow-hidden">
              <thead>
                <tr className="bg-sky-100">
                  <th className="text-left p-4 font-bold text-slate-800">Shipping Method</th>
                  <th className="text-left p-4 font-bold text-slate-800">Delivery Time</th>
                  <th className="text-left p-4 font-bold text-slate-800">Cost</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                <tr className="border-t border-slate-200">
                  <td className="p-4">Standard Shipping</td>
                  <td className="p-4">5-7 Business Days</td>
                  <td className="p-4">$5.99 (Free over $50)</td>
                </tr>
                <tr className="border-t border-slate-200">
                  <td className="p-4">Expedited Shipping</td>
                  <td className="p-4">3-4 Business Days</td>
                  <td className="p-4">$12.99</td>
                </tr>
                <tr className="border-t border-slate-200">
                  <td className="p-4">Express Shipping</td>
                  <td className="p-4">1-2 Business Days</td>
                  <td className="p-4">$24.99</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-slate-600 mb-6">
            <strong>Note:</strong> Alaska, Hawaii, and US Territories may have extended delivery times and additional shipping charges.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            3. Order Processing Time
          </h2>
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <Package className="text-sky-500" size={24} />
              <h3 className="font-bold text-slate-800">Processing Timeline</h3>
            </div>
            <ul className="text-slate-600 space-y-2">
              <li>• Orders placed before 2:00 PM EST are processed the same business day</li>
              <li>• Orders placed after 2:00 PM EST are processed the next business day</li>
              <li>• Processing time: 1-2 business days</li>
              <li>• Weekend and holiday orders are processed on the next business day</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            4. Order Tracking
          </h2>
          <p className="text-slate-600 mb-6">
            Once your order ships, you'll receive a confirmation email with a tracking number. You can track your package using the link provided in the email or by visiting our carrier's website directly. Please allow 24-48 hours for tracking information to update after shipment.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            5. Shipping Carriers
          </h2>
          <p className="text-slate-600 mb-6">
            We partner with trusted shipping carriers including:
          </p>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li>USPS (United States Postal Service)</li>
            <li>UPS (United Parcel Service)</li>
            <li>FedEx</li>
          </ul>
          <p className="text-slate-600 mb-6">
            The carrier used depends on your location and the shipping method selected. We reserve the right to choose the most efficient carrier for your order.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            6. Delivery Information
          </h2>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li><strong>Signature:</strong> Packages over $100 may require a signature upon delivery.</li>
            <li><strong>Address Accuracy:</strong> Please ensure your shipping address is complete and accurate. We are not responsible for packages delivered to incorrect addresses due to customer error.</li>
            <li><strong>Apartment/Suite Numbers:</strong> Please include all necessary address details including apartment, suite, or unit numbers.</li>
            <li><strong>PO Boxes:</strong> We can ship to PO Boxes via USPS for standard shipping only.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            7. Delivery Issues
          </h2>
          <p className="text-slate-600 mb-4">If you experience any delivery issues, please contact us:</p>
          <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
            <li><strong>Lost Package:</strong> If your tracking shows delivered but you haven't received it, contact us within 7 days.</li>
            <li><strong>Damaged Package:</strong> Refuse delivery if the package appears damaged, or contact us within 48 hours with photos.</li>
            <li><strong>Missing Items:</strong> If items are missing from your order, contact us within 48 hours of delivery.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            8. Holiday Shipping
          </h2>
          <p className="text-slate-600 mb-6">
            During peak seasons (holidays, sales events), shipping times may be extended due to high volume. We recommend ordering early during these periods. Check our website for holiday shipping deadlines to ensure timely delivery.
          </p>

          <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            9. Contact Us
          </h2>
          <p className="text-slate-600 mb-4">
            For any questions about shipping, please contact our customer service team:
          </p>
          <div className="bg-slate-50 rounded-xl p-6">
            <ul className="text-slate-600 space-y-2">
              <li><strong>Email:</strong> support@cartlixs.store</li>
              <li><strong>Phone:</strong> +1-888-435-6822</li>
              <li><strong>Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM EST</li>
              <li><strong>Address:</strong> 192 A 22nd St #4699, New York, NY 30272</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingPolicy;
